import React, { useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import {
  FacebookLogoIcon,
  InstagramLogoIcon,
  LinkedinLogoIcon,
  PhoneCallIcon,
  PhoneIncomingIcon,
  WhatsappLogoIcon,
  XLogoIcon,
  YoutubeLogoIcon,
} from '@/components/icons';

const footerSections = [
  {
    title: 'aboutMOE',
    links: [
      ['moeStrategy', '/about-us/pages/moe-strategy.aspx'],
      ['organizationStructure', '/about-us/pages/organisational-structure.aspx'],
      ['customerHappinessCharter', '/pages/customer-happiness-charter.aspx'],
      ['uaeGovernmentCharter', '/pages/the-uae-government-charter.aspx'],
      ['careers', '/more/pages/careers.aspx'],
    ],
  },
  {
    title: 'helpAndSupport',
    links: [
      ['contactUs', '/pages/contact-us.aspx'],
      ['faqs', '/more/pages/faqs.aspx'],
      ['digitalAccessibilityStatement', '/digital-participation/policies/pages/digital-accessibility-policy.aspx'],
      ['sitemap', '/pages/sitemap.aspx'],
    ],
  },
  {
    title: 'references',
    links: [
      ['guides', '/guides/pages/default.aspx'],
      ['portals', '/more/portals/pages/default.aspx'],
      ['legislations', '/about-us/legislations/Pages/default.aspx'],
      ['quickLinks', '/more/important-links/pages/default.aspx'],
    ],
  },
  {
    title: 'aboutThisWebsite',
    links: [
      ['copyright', '/pages/copyright.aspx'],
      ['disclaimer', '/pages/disclaimer.aspx'],
      ['privacyPolicy', '/pages/privacy-policy.aspx'],
      ['termsAndConditions', '/pages/terms-and-conditions.aspx'],
    ],
  },
];

export default function Footer() {
  const { t, locale } = useI18n() as any;
  const [open, setOpen] = useState(0);
  const moeURL = 'https://www.moe.gov.ae/';
  const year = new Date().getFullYear();

  return (
    <footer className="aegov-footer">
      <div className="footer-top sm:py-6 md:py-12">
        <div className="container lg:max-w-7xl mx-auto">
          <div className="footer-top-left sm:flex gap-3 xl:px-6">
            <nav className="aegov-accordion aegov-mobile-accordion flex-1">
              <ul className="grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-x-2.5 gap-y-1">
                {footerSections.map((section, index) => (
                  <li key={section.title}>
                    <div className="accordion-title sm:mb-4">
                      <button
                        type="button"
                        onClick={() => setOpen(open === index ? -1 : index)}
                        className="
                          max-sm:py-4 max-sm:flex justify-between
                          sm:pointer-events-none
                          text-gray-900
                        "
                      >
                        <span>{t[section.title]}</span>

                        <svg
                          className={`sm:hidden transition-transform ${open === index ? 'rotate-45' : ''}`}
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      </button>
                    </div>

                    <div className={`accordion-content ${open === index ? 'block' : 'hidden'} sm:block`}>
                      <ul className="accordion-content-body">
                        {section.links.map(([label, url]) => (
                          <li key={label}>
                            <a
                              href={`${moeURL}${locale}${url}`}
                              className="text-sm text-blue-600 border-0 hover:border-0"
                            >
                              {t[label]}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="text-center footer-contact lg:w-48 xl:w-[277px]">
              <ul className="divide-y divide-aeblack-100">
                <li>
                  <a href="https://171.ae/">
                    <img
                      src="/img/tawasul.png"
                      alt="tawasul"
                      className="inline-block w-36 lg:w-auto"
                      width={100}
                      height={100}
                    />
                  </a>
                </li>

                <li className="">
                  <a href="tel:171" className="inline-with-gap pr-4">
                    <PhoneCallIcon weight="fill" fill="green" />
                    <span>171</span>
                  </a>

                  <a href="https://wa.me/97147771000" className="inline-with-gap">
                    <WhatsappLogoIcon weight="fill" fill="green" />
                    <span>04-7771777</span>
                  </a>
                </li>

                <li className="inline-with-gap">
                  <span className="text-aeblack-500 inline-with-gap">
                    <PhoneIncomingIcon />
                    {t['tollFreeNumber']}
                  </span>

                  <a href="tel:80051115">80051115</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom py-6 md:py-12">
        <div className="container lg:max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-y-6 items-center justify-between">
            <div className="w-full lg:w-7/12">
              <div className="text-aeblack-700 text-xs sm:text-sm">
                {t['allRightsReserved']}
                {' © '}
                {t['ministryOfEducation']} {year}
              </div>
            </div>

            <div className="w-full lg:w-5/12 social-sharing max-md:w-full justify-center lg:justify-end">
              <span className="text-sm text-aeblack-700 max-sm:hidden">{t['followUsOn']}</span>
              <ul className="flex items-center gap-6">
                <li>
                  <a href="https://www.facebook.com/MOEUAEofficial">
                    <FacebookLogoIcon />
                    <span className="sr-only">facebook</span>
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/MOEUAEofficial/">
                    <InstagramLogoIcon />
                    <span className="sr-only">instagram</span>
                  </a>
                </li>
                <li>
                  <a href="https://www.linkedin.com/company/moeuaeofficial">
                    <LinkedinLogoIcon />
                    <span className="sr-only">LinkedIn</span>
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com/MOEUAEofficial">
                    <XLogoIcon />
                    <span className="sr-only">twitter</span>
                  </a>
                </li>
                <li>
                  <a href="https://www.youtube.com/c/MOEUAEofficial">
                    <YoutubeLogoIcon />
                    <span className="sr-only">YouTube</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
